import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  AlertTriangle, Plus, Edit, Trash2, Search, X, Loader2, Save, 
  Calendar, AlertOctagon, CheckCircle2, FileText, RefreshCw, 
  BarChart3, Target, Eye, Download, Link as LinkIcon, ExternalLink,
  ShieldAlert, Activity, Clock, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ========== RISK MATRIX COMPONENT ==========
const RiskMatrix = ({ matrixData, risks }) => {
  const severityLabels = ['Insignificant', 'Minor', 'Moderate', 'Major', 'Catastrophic'];
  const likelihoodLabels = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'];
  
  const getCellColor = (l, s) => {
    const score = (l + 1) * (s + 1);
    if (score >= 16) return 'bg-red-600/80 border-red-500';
    if (score >= 11) return 'bg-orange-500/70 border-orange-400';
    if (score >= 6) return 'bg-yellow-500/60 border-yellow-400';
    return 'bg-green-500/50 border-green-400';
  };

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5" data-testid="risk-matrix">
      <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
        <Target className="w-4 h-4 text-red-400" /> Risk Matrix (5x5)
      </h3>
      <div className="flex gap-1">
        <div className="flex flex-col justify-between py-1 pr-2 text-right" style={{width: '90px'}}>
          {[...likelihoodLabels].reverse().map((label, i) => (
            <div key={i} className="text-[10px] text-slate-500 h-10 flex items-center justify-end leading-tight">
              {5 - i}. {label}
            </div>
          ))}
          <div className="h-5" />
        </div>
        <div className="flex-1">
          <div className="grid grid-cols-5 gap-1">
            {[4, 3, 2, 1, 0].map(l => (
              [0, 1, 2, 3, 4].map(s => {
                const count = matrixData?.[l]?.[s] || 0;
                const score = (l + 1) * (s + 1);
                return (
                  <div
                    key={`${l}-${s}`}
                    className={`h-10 rounded border flex items-center justify-center text-xs font-bold transition-all ${getCellColor(l, s)} ${count > 0 ? 'ring-2 ring-white/40 scale-105' : 'opacity-60'}`}
                    title={`L${l+1} x S${s+1} = ${score} | ${count} risk(s)`}
                  >
                    {count > 0 ? <span className="text-white drop-shadow">{count}</span> : <span className="text-white/30">{score}</span>}
                  </div>
                );
              })
            ))}
          </div>
          <div className="grid grid-cols-5 gap-1 mt-1">
            {severityLabels.map((label, i) => (
              <div key={i} className="text-[10px] text-slate-500 text-center leading-tight">{i+1}. {label}</div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-between mt-3 text-[10px] text-slate-600">
        <span>LIKELIHOOD (Y-axis)</span>
        <span>SEVERITY (X-axis)</span>
      </div>
    </div>
  );
};

// ========== DEPARTMENT CHART COMPONENT ==========
const DepartmentChart = ({ departmentData }) => {
  const entries = Object.entries(departmentData || {}).sort((a, b) => b[1].total - a[1].total);
  const maxTotal = Math.max(...entries.map(([, v]) => v.total), 1);

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5" data-testid="department-chart">
      <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-red-400" /> Risks by Department
      </h3>
      <div className="space-y-3">
        {entries.map(([dept, data]) => (
          <div key={dept}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-300">{dept}</span>
              <span className="text-slate-500">{data.total}</span>
            </div>
            <div className="h-4 bg-slate-800 rounded-full overflow-hidden flex">
              {data.Extreme > 0 && <div className="bg-red-500 h-full" style={{width: `${(data.Extreme / maxTotal) * 100}%`}} />}
              {data.High > 0 && <div className="bg-orange-500 h-full" style={{width: `${(data.High / maxTotal) * 100}%`}} />}
              {data.Medium > 0 && <div className="bg-yellow-500 h-full" style={{width: `${(data.Medium / maxTotal) * 100}%`}} />}
              {data.Low > 0 && <div className="bg-green-500 h-full" style={{width: `${(data.Low / maxTotal) * 100}%`}} />}
            </div>
          </div>
        ))}
        {entries.length === 0 && <p className="text-slate-500 text-sm">No department data</p>}
      </div>
      <div className="flex gap-3 mt-4 text-[10px]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Extreme</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500" />High</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" />Medium</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />Low</span>
      </div>
    </div>
  );
};

// ========== MAIN COMPONENT ==========
const RiskManager = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [risks, setRisks] = useState([]);
  const [activeView, setActiveView] = useState('register');
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [linkedData, setLinkedData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRating, setFilterRating] = useState('all');
  const [showRiskDialog, setShowRiskDialog] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const [riskForm, setRiskForm] = useState({
    title: '', category: 'Operational', department: '', likelihood: 3, severity: 3,
    owner: '', review_date: '', description: '', root_cause: '', potential_impact: '',
    existing_controls: '', linked_works_order: '', linked_ncr: '', residual_likelihood: '', residual_severity: ''
  });
  
  const [actionForm, setActionForm] = useState({ description: '', owner: '', target_date: '', notes: '' });

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, statsRes, risksRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/risks/config/options`),
        axios.get(`${BACKEND_URL}/api/risks/stats`),
        axios.get(`${BACKEND_URL}/api/risks`)
      ]);
      setConfig(configRes.data);
      setStats(statsRes.data);
      setRisks(risksRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load risk data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchAllData();
  }, [isOpen, fetchAllData]);

  const getRatingColor = (rating) => {
    switch (rating) {
      case 'Low': return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'Medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      case 'High': return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 'Extreme': return 'bg-red-500/20 text-red-400 border-red-500/50';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/50';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'bg-blue-500/20 text-blue-400';
      case 'Mitigated': return 'bg-amber-500/20 text-amber-400';
      case 'Closed': return 'bg-green-500/20 text-green-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  const calculateScore = (likelihood, severity) => likelihood * severity;
  const getCalculatedRating = (score) => {
    if (score <= 5) return 'Low';
    if (score <= 10) return 'Medium';
    if (score <= 15) return 'High';
    return 'Extreme';
  };

  const filteredRisks = risks.filter(risk => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!risk.risk_id?.toLowerCase().includes(q) && !risk.title?.toLowerCase().includes(q) &&
          !risk.department?.toLowerCase().includes(q) && !risk.owner?.toLowerCase().includes(q)) return false;
    }
    if (filterCategory !== 'all' && risk.category !== filterCategory) return false;
    if (filterStatus !== 'all' && risk.status !== filterStatus) return false;
    if (filterRating !== 'all' && risk.risk_rating !== filterRating) return false;
    return true;
  });

  const handleSaveRisk = async () => {
    if (!riskForm.title || !riskForm.department || !riskForm.owner || !riskForm.review_date || !riskForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...riskForm,
        likelihood: parseInt(riskForm.likelihood),
        severity: parseInt(riskForm.severity),
        residual_likelihood: riskForm.residual_likelihood ? parseInt(riskForm.residual_likelihood) : null,
        residual_severity: riskForm.residual_severity ? parseInt(riskForm.residual_severity) : null
      };
      if (editingRisk) {
        await axios.put(`${BACKEND_URL}/api/risks/${editingRisk.risk_id}`, payload);
        toast.success('Risk updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/risks`, payload);
        toast.success('Risk created successfully');
      }
      setShowRiskDialog(false);
      setEditingRisk(null);
      resetRiskForm();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save risk');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRisk = async (riskId) => {
    if (!window.confirm('Are you sure you want to delete this risk?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/risks/${riskId}`);
      toast.success('Risk deleted');
      setShowDetailDialog(false);
      setSelectedRisk(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete risk');
    }
  };

  const handleAddAction = async () => {
    if (!actionForm.description || !actionForm.owner || !actionForm.target_date) {
      toast.error('Please fill in all required action fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/risks/${selectedRisk.risk_id}/actions`, actionForm);
      toast.success('Action added');
      setShowActionDialog(false);
      setActionForm({ description: '', owner: '', target_date: '', notes: '' });
      const res = await axios.get(`${BACKEND_URL}/api/risks/${selectedRisk.risk_id}`);
      setSelectedRisk(res.data);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to add action');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateActionStatus = async (actionId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/risks/${selectedRisk.risk_id}/actions/${actionId}`, { status: newStatus });
      toast.success('Action updated');
      const res = await axios.get(`${BACKEND_URL}/api/risks/${selectedRisk.risk_id}`);
      setSelectedRisk(res.data);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update action');
    }
  };

  const handleExportPDF = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/risks/export/register-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Risk_Register_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Risk Register PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const resetRiskForm = () => {
    setRiskForm({
      title: '', category: 'Operational', department: '', likelihood: 3, severity: 3,
      owner: '', review_date: '', description: '', root_cause: '', potential_impact: '',
      existing_controls: '', linked_works_order: '', linked_ncr: '', residual_likelihood: '', residual_severity: ''
    });
  };

  const openEditRisk = (risk) => {
    setEditingRisk(risk);
    setRiskForm({
      title: risk.title, category: risk.category, department: risk.department,
      likelihood: risk.likelihood, severity: risk.severity, owner: risk.owner,
      review_date: risk.review_date, description: risk.description,
      root_cause: risk.root_cause || '', potential_impact: risk.potential_impact || '',
      existing_controls: risk.existing_controls || '',
      linked_works_order: risk.linked_works_order || '', linked_ncr: risk.linked_ncr || '',
      residual_likelihood: risk.residual_likelihood?.toString() || '',
      residual_severity: risk.residual_severity?.toString() || ''
    });
    setShowRiskDialog(true);
  };

  const openRiskDetail = async (risk) => {
    setSelectedRisk(risk);
    setLinkedData(null);
    setShowDetailDialog(true);
    // Fetch linked data
    if (risk.linked_ncr || risk.linked_works_order) {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/risks/${risk.risk_id}/linked-data`);
        setLinkedData(res.data);
      } catch (err) {
        console.error('Failed to fetch linked data');
      }
    }
  };

  if (!isOpen) return null;

  const previewScore = calculateScore(parseInt(riskForm.likelihood) || 1, parseInt(riskForm.severity) || 1);
  const previewRating = getCalculatedRating(previewScore);

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-md overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-950/90 backdrop-blur-xl border-b border-red-500/20">
        <div className="max-w-[1920px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center">
              <AlertOctagon className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white uppercase tracking-wide" data-testid="risk-manager-title">Risk Manager</h1>
              <p className="text-sm text-slate-500">ISO9001:2015 Risk Assessment & Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex bg-slate-800/50 rounded-lg p-1">
              {['register', 'dashboard'].map(view => (
                <button key={view} onClick={() => setActiveView(view)}
                  data-testid={`view-${view}-btn`}
                  className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wider rounded transition-colors ${
                    activeView === view ? 'bg-red-500/20 text-red-400' : 'text-slate-400 hover:text-white'
                  }`}>{view}</button>
              ))}
            </div>
            <Button onClick={handleExportPDF} disabled={exportingPdf} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="export-pdf-btn">
              {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
            <Button onClick={onClose} variant="ghost" className="text-slate-400 hover:text-white" data-testid="close-risk-manager-btn">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-red-400" /></div>
      ) : (
        <div className="max-w-[1920px] mx-auto px-6 py-6">
          {activeView === 'dashboard' ? <DashboardView stats={stats} risks={risks} getRatingColor={getRatingColor} /> : (
            <RegisterView
              risks={filteredRisks} searchQuery={searchQuery} setSearchQuery={setSearchQuery}
              filterCategory={filterCategory} setFilterCategory={setFilterCategory}
              filterStatus={filterStatus} setFilterStatus={setFilterStatus}
              filterRating={filterRating} setFilterRating={setFilterRating}
              config={config} fetchAllData={fetchAllData} setEditingRisk={setEditingRisk}
              resetRiskForm={resetRiskForm} setShowRiskDialog={setShowRiskDialog}
              openRiskDetail={openRiskDetail} openEditRisk={openEditRisk}
              handleDeleteRisk={handleDeleteRisk} getRatingColor={getRatingColor} getStatusColor={getStatusColor}
            />
          )}
        </div>
      )}

      {/* Add/Edit Risk Dialog */}
      <RiskFormDialog
        open={showRiskDialog} onOpenChange={setShowRiskDialog}
        editingRisk={editingRisk} riskForm={riskForm} setRiskForm={setRiskForm}
        config={config} previewScore={previewScore} previewRating={previewRating}
        handleSaveRisk={handleSaveRisk} submitting={submitting} getRatingColor={getRatingColor}
      />

      {/* Risk Detail Dialog */}
      <RiskDetailDialog
        open={showDetailDialog} onOpenChange={setShowDetailDialog}
        selectedRisk={selectedRisk} linkedData={linkedData}
        handleDeleteRisk={handleDeleteRisk} openEditRisk={openEditRisk}
        setShowDetailDialog={setShowDetailDialog} setShowActionDialog={setShowActionDialog}
        handleUpdateActionStatus={handleUpdateActionStatus}
        getRatingColor={getRatingColor} getStatusColor={getStatusColor}
      />

      {/* Add Action Dialog */}
      <ActionFormDialog
        open={showActionDialog} onOpenChange={setShowActionDialog}
        actionForm={actionForm} setActionForm={setActionForm}
        handleAddAction={handleAddAction} submitting={submitting}
      />
    </div>
  );
};

// ========== DASHBOARD VIEW ==========
const DashboardView = ({ stats, risks, getRatingColor }) => (
  <div className="space-y-6" data-testid="risk-dashboard">
    {/* KPI Cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
      {[
        { label: 'Total Risks', value: stats?.total_risks || 0, color: 'border-slate-700', text: 'text-white' },
        { label: 'Open', value: stats?.open_risks || 0, color: 'border-blue-500/30', text: 'text-blue-400' },
        { label: 'High/Extreme', value: stats?.high_extreme_count || 0, color: 'border-red-500/30', text: 'text-red-400' },
        { label: 'Overdue Actions', value: stats?.overdue_actions || 0, color: 'border-amber-500/30', text: 'text-amber-400' },
        { label: 'Mitigated', value: stats?.by_status?.Mitigated || 0, color: 'border-amber-500/30', text: 'text-amber-400' },
        { label: 'Closed', value: stats?.by_status?.Closed || 0, color: 'border-green-500/30', text: 'text-green-400' },
        { label: 'Linked NCRs', value: stats?.linked_ncrs || 0, color: 'border-purple-500/30', text: 'text-purple-400' },
        { label: 'Due for Review', value: stats?.risks_needing_review || 0, color: 'border-cyan-500/30', text: 'text-cyan-400' },
      ].map((kpi, i) => (
        <div key={i} className={`bg-slate-900/50 border ${kpi.color} rounded-xl p-4`} data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">{kpi.label}</p>
          <p className={`text-2xl font-bold ${kpi.text}`}>{kpi.value}</p>
        </div>
      ))}
    </div>

    {/* Charts Row - Risk Matrix + Department */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <RiskMatrix matrixData={stats?.risk_matrix} risks={risks} />
      <DepartmentChart departmentData={stats?.by_department} />
    </div>

    {/* Middle Row - Rating + Category + Actions */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* By Rating */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Risks by Rating</h3>
        <div className="space-y-3">
          {['Low', 'Medium', 'High', 'Extreme'].map(rating => {
            const count = stats?.by_rating?.[rating] || 0;
            const total = stats?.total_risks || 1;
            const pct = Math.round((count / total) * 100);
            return (
              <div key={rating}>
                <div className="flex justify-between text-sm mb-1">
                  <span className={`${rating === 'Low' ? 'text-green-400' : rating === 'Medium' ? 'text-yellow-400' : rating === 'High' ? 'text-orange-400' : 'text-red-400'}`}>{rating}</span>
                  <span className="text-slate-400">{count} ({pct}%)</span>
                </div>
                <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${
                    rating === 'Low' ? 'bg-green-500' : rating === 'Medium' ? 'bg-yellow-500' : rating === 'High' ? 'bg-orange-500' : 'bg-red-500'
                  }`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* By Category */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Risks by Category</h3>
        <div className="space-y-2">
          {Object.entries(stats?.by_category || {}).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
            <div key={cat} className="flex justify-between items-center p-2 bg-slate-800/30 rounded-lg">
              <span className="text-sm text-slate-300">{cat}</span>
              <span className="text-sm font-bold text-white bg-slate-700/50 px-2 py-0.5 rounded">{count}</span>
            </div>
          ))}
          {Object.keys(stats?.by_category || {}).length === 0 && <p className="text-slate-500 text-sm">No risks recorded</p>}
        </div>
      </div>

      {/* Actions Summary */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
          <Activity className="w-4 h-4 text-red-400" /> Actions Summary
        </h3>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-3 bg-slate-800/40 rounded-lg">
            <p className="text-xl font-bold text-white">{stats?.total_actions || 0}</p>
            <p className="text-[10px] text-slate-500 uppercase">Total</p>
          </div>
          <div className="text-center p-3 bg-green-500/10 rounded-lg">
            <p className="text-xl font-bold text-green-400">{stats?.completed_actions || 0}</p>
            <p className="text-[10px] text-slate-500 uppercase">Done</p>
          </div>
          <div className="text-center p-3 bg-amber-500/10 rounded-lg">
            <p className="text-xl font-bold text-amber-400">{stats?.pending_actions || 0}</p>
            <p className="text-[10px] text-slate-500 uppercase">Pending</p>
          </div>
        </div>
        {stats?.total_actions > 0 && (
          <div className="h-3 bg-slate-800 rounded-full overflow-hidden flex">
            <div className="bg-green-500 h-full transition-all" style={{width: `${(stats.completed_actions / stats.total_actions) * 100}%`}} />
            <div className="bg-amber-500 h-full transition-all" style={{width: `${(stats.pending_actions / stats.total_actions) * 100}%`}} />
          </div>
        )}
      </div>
    </div>

    {/* Bottom Row - Top Risks + Overdue Actions */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Top 10 Risks */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-red-400" /> Top 10 Highest Risks
        </h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {stats?.top_risks?.map((risk, idx) => (
            <div key={risk.risk_id} className="flex items-center gap-3 p-2.5 bg-slate-800/40 rounded-lg hover:bg-slate-800/60 transition-colors">
              <span className="text-xs text-slate-600 w-5 text-right font-mono">{idx + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-mono text-slate-500">{risk.risk_id}</p>
                <p className="text-sm text-white truncate">{risk.title}</p>
                <p className="text-[10px] text-slate-600">{risk.department} | {risk.owner}</p>
              </div>
              <span className={`text-xs px-2 py-1 rounded border font-bold ${getRatingColor(risk.rating)}`}>{risk.score}</span>
            </div>
          ))}
          {(!stats?.top_risks || stats.top_risks.length === 0) && <p className="text-slate-500 text-sm">No risks recorded</p>}
        </div>
      </div>

      {/* Overdue Actions */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-400" /> Overdue Actions
        </h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {stats?.overdue_action_list?.map((action, idx) => (
            <div key={idx} className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono text-red-400">{action.risk_id}</p>
                  <p className="text-sm text-white truncate">{action.action_description}</p>
                  <p className="text-xs text-slate-500">{action.action_owner} | Due: {action.target_date}</p>
                </div>
                <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded font-bold whitespace-nowrap">
                  {action.days_overdue}d overdue
                </span>
              </div>
            </div>
          ))}
          {(!stats?.overdue_action_list || stats.overdue_action_list.length === 0) && (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No overdue actions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

// ========== REGISTER VIEW ==========
const RegisterView = ({ risks, searchQuery, setSearchQuery, filterCategory, setFilterCategory,
  filterStatus, setFilterStatus, filterRating, setFilterRating, config, fetchAllData,
  setEditingRisk, resetRiskForm, setShowRiskDialog, openRiskDetail, openEditRisk,
  handleDeleteRisk, getRatingColor, getStatusColor }) => (
  <div className="space-y-4" data-testid="risk-register">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-3 flex-1">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search risks..." className="pl-10 bg-slate-800/50 border-slate-700" data-testid="risk-search-input" />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-36 bg-slate-800/50 border-slate-700"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {config?.categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-32 bg-slate-800/50 border-slate-700"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {config?.statuses?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterRating} onValueChange={setFilterRating}>
          <SelectTrigger className="w-32 bg-slate-800/50 border-slate-700"><SelectValue placeholder="Rating" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Ratings</SelectItem>
            {config?.ratings?.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchAllData} className="border-slate-700"><RefreshCw className="w-4 h-4" /></Button>
      </div>
      <Button onClick={() => { setEditingRisk(null); resetRiskForm(); setShowRiskDialog(true); }} className="bg-red-600 hover:bg-red-500" data-testid="add-risk-btn">
        <Plus className="w-4 h-4 mr-2" /> Add Risk
      </Button>
    </div>

    <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="risk-register-table">
          <thead>
            <tr className="bg-slate-800/50 border-b border-slate-700">
              {['Risk ID', 'Title', 'Category', 'Department', 'L', 'S', 'Score', 'Rating', 'Status', 'Owner', 'Review', 'Actions'].map(h => (
                <th key={h} className={`text-${h === 'Actions' ? 'right' : ['L', 'S', 'Score', 'Rating'].includes(h) ? 'center' : 'left'} text-xs text-slate-500 uppercase tracking-wider px-4 py-3`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {risks.map(risk => (
              <tr key={risk.risk_id} className={`hover:bg-slate-800/30 cursor-pointer ${risk.risk_rating === 'Extreme' ? 'bg-red-500/5' : risk.risk_rating === 'High' ? 'bg-orange-500/5' : ''}`} onClick={() => openRiskDetail(risk)} data-testid={`risk-row-${risk.risk_id}`}>
                <td className="px-4 py-3 text-sm font-mono text-slate-300">{risk.risk_id}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-white font-medium truncate max-w-xs">{risk.title}</p>
                    {(risk.linked_ncr || risk.linked_works_order) && <LinkIcon className="w-3 h-3 text-purple-400 flex-shrink-0" title="Has linked records" />}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-400">{risk.category}</td>
                <td className="px-4 py-3 text-sm text-slate-400">{risk.department}</td>
                <td className="px-4 py-3 text-sm text-center text-slate-300">{risk.likelihood}</td>
                <td className="px-4 py-3 text-sm text-center text-slate-300">{risk.severity}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-sm font-bold ${risk.risk_score >= 16 ? 'text-red-400' : risk.risk_score >= 11 ? 'text-orange-400' : risk.risk_score >= 6 ? 'text-yellow-400' : 'text-green-400'}`}>{risk.risk_score}</span>
                </td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded border font-medium ${getRatingColor(risk.risk_rating)}`}>{risk.risk_rating}</span></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${getStatusColor(risk.status)}`}>{risk.status}</span></td>
                <td className="px-4 py-3 text-sm text-slate-400">{risk.owner}</td>
                <td className="px-4 py-3 text-sm text-slate-500">{risk.review_date}</td>
                <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEditRisk(risk)} className="text-blue-400 hover:bg-blue-500/10 h-7 w-7 p-0"><Edit className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteRisk(risk.risk_id)} className="text-red-400 hover:bg-red-500/10 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {risks.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No risks found</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ========== RISK FORM DIALOG ==========
const RiskFormDialog = ({ open, onOpenChange, editingRisk, riskForm, setRiskForm, config, previewScore, previewRating, handleSaveRisk, submitting, getRatingColor }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-slate-900 border-red-500/30 max-w-3xl max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
          <AlertOctagon className="w-5 h-5 text-red-400" />
          {editingRisk ? 'Edit Risk' : 'Add New Risk'}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 pt-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="text-slate-400 uppercase text-xs">Title *</Label>
            <Input value={riskForm.title} onChange={(e) => setRiskForm({ ...riskForm, title: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" placeholder="Risk title" data-testid="risk-title-input" />
          </div>
          <div>
            <Label className="text-slate-400 uppercase text-xs">Category *</Label>
            <Select value={riskForm.category} onValueChange={(v) => setRiskForm({ ...riskForm, category: v })}>
              <SelectTrigger className="bg-slate-800 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{config?.categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-400 uppercase text-xs">Department *</Label>
            <Input value={riskForm.department} onChange={(e) => setRiskForm({ ...riskForm, department: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" placeholder="e.g., Production, Quality" data-testid="risk-department-input" />
          </div>
        </div>

        <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Risk Assessment</p>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label className="text-slate-400 uppercase text-xs">Likelihood (1-5) *</Label>
              <Select value={riskForm.likelihood.toString()} onValueChange={(v) => setRiskForm({ ...riskForm, likelihood: parseInt(v) })}>
                <SelectTrigger className="bg-slate-900 border-slate-600 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{config?.likelihood_scale?.map(l => <SelectItem key={l.value} value={l.value.toString()}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Severity (1-5) *</Label>
              <Select value={riskForm.severity.toString()} onValueChange={(v) => setRiskForm({ ...riskForm, severity: parseInt(v) })}>
                <SelectTrigger className="bg-slate-900 border-slate-600 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{config?.severity_scale?.map(s => <SelectItem key={s.value} value={s.value.toString()}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Score</Label>
              <div className={`mt-1 p-2 rounded border text-center text-xl font-bold ${
                previewScore >= 16 ? 'bg-red-500/20 border-red-500/50 text-red-400' :
                previewScore >= 11 ? 'bg-orange-500/20 border-orange-500/50 text-orange-400' :
                previewScore >= 6 ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400' :
                'bg-green-500/20 border-green-500/50 text-green-400'
              }`} data-testid="risk-score-preview">{previewScore}</div>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Rating</Label>
              <div className={`mt-1 p-2 rounded border text-center text-sm font-bold ${getRatingColor(previewRating)}`} data-testid="risk-rating-preview">{previewRating}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-400 uppercase text-xs">Owner *</Label>
            <Input value={riskForm.owner} onChange={(e) => setRiskForm({ ...riskForm, owner: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" placeholder="Responsible person" data-testid="risk-owner-input" />
          </div>
          <div>
            <Label className="text-slate-400 uppercase text-xs">Review Date *</Label>
            <Input type="date" value={riskForm.review_date} onChange={(e) => setRiskForm({ ...riskForm, review_date: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" data-testid="risk-review-date-input" />
          </div>
        </div>

        <div>
          <Label className="text-slate-400 uppercase text-xs">Description *</Label>
          <Textarea value={riskForm.description} onChange={(e) => setRiskForm({ ...riskForm, description: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" placeholder="Detailed risk description..." rows={3} data-testid="risk-description-input" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-400 uppercase text-xs">Root Cause</Label>
            <Textarea value={riskForm.root_cause} onChange={(e) => setRiskForm({ ...riskForm, root_cause: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" rows={2} />
          </div>
          <div>
            <Label className="text-slate-400 uppercase text-xs">Potential Impact</Label>
            <Textarea value={riskForm.potential_impact} onChange={(e) => setRiskForm({ ...riskForm, potential_impact: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" rows={2} />
          </div>
        </div>

        <div>
          <Label className="text-slate-400 uppercase text-xs">Existing Controls</Label>
          <Textarea value={riskForm.existing_controls} onChange={(e) => setRiskForm({ ...riskForm, existing_controls: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" rows={2} />
        </div>

        {/* Integration Links */}
        <div className="p-4 bg-purple-500/5 rounded-lg border border-purple-500/20">
          <p className="text-xs text-purple-400 uppercase tracking-wider mb-3 flex items-center gap-2"><LinkIcon className="w-3 h-3" /> Cross-Module Integration</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-400 uppercase text-xs">Linked Works Order</Label>
              <Input value={riskForm.linked_works_order} onChange={(e) => setRiskForm({ ...riskForm, linked_works_order: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" placeholder="e.g., WO-001" data-testid="risk-linked-wo-input" />
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Linked NCR</Label>
              <Input value={riskForm.linked_ncr} onChange={(e) => setRiskForm({ ...riskForm, linked_ncr: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" placeholder="e.g., NCR-2026-0001" data-testid="risk-linked-ncr-input" />
            </div>
          </div>
        </div>

        {editingRisk && (
          <div className="p-4 bg-green-500/5 rounded-lg border border-green-500/20">
            <p className="text-xs text-green-400 uppercase tracking-wider mb-3">Residual Risk (After Mitigation)</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Residual Likelihood</Label>
                <Select value={riskForm.residual_likelihood} onValueChange={(v) => setRiskForm({ ...riskForm, residual_likelihood: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{config?.likelihood_scale?.map(l => <SelectItem key={l.value} value={l.value.toString()}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Residual Severity</Label>
                <Select value={riskForm.residual_severity} onValueChange={(v) => setRiskForm({ ...riskForm, residual_severity: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{config?.severity_scale?.map(s => <SelectItem key={s.value} value={s.value.toString()}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-600">Cancel</Button>
          <Button onClick={handleSaveRisk} disabled={submitting} className="bg-red-600 hover:bg-red-500" data-testid="save-risk-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {editingRisk ? 'Update Risk' : 'Create Risk'}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// ========== RISK DETAIL DIALOG ==========
const RiskDetailDialog = ({ open, onOpenChange, selectedRisk, linkedData, handleDeleteRisk, openEditRisk, setShowDetailDialog, setShowActionDialog, handleUpdateActionStatus, getRatingColor, getStatusColor }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-slate-900 border-red-500/30 max-w-4xl max-h-[85vh] overflow-y-auto">
      {selectedRisk && (
        <>
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${
                selectedRisk.risk_rating === 'Extreme' ? 'bg-red-500' : selectedRisk.risk_rating === 'High' ? 'bg-orange-500' : selectedRisk.risk_rating === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'
              }`} />
              {selectedRisk.risk_id}
            </DialogTitle>
            <p className="text-lg text-slate-300">{selectedRisk.title}</p>
          </DialogHeader>
          
          <div className="space-y-5 pt-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-5 gap-3">
              <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                <p className="text-[10px] text-slate-500 uppercase">Category</p>
                <p className="text-sm text-white font-medium">{selectedRisk.category}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                <p className="text-[10px] text-slate-500 uppercase">Score</p>
                <p className={`text-2xl font-bold ${selectedRisk.risk_score >= 16 ? 'text-red-400' : selectedRisk.risk_score >= 11 ? 'text-orange-400' : selectedRisk.risk_score >= 6 ? 'text-yellow-400' : 'text-green-400'}`}>{selectedRisk.risk_score}</p>
              </div>
              <div className={`rounded-lg p-3 text-center ${getRatingColor(selectedRisk.risk_rating)}`}>
                <p className="text-[10px] uppercase opacity-75">Rating</p>
                <p className="text-lg font-bold">{selectedRisk.risk_rating}</p>
              </div>
              <div className={`rounded-lg p-3 text-center ${getStatusColor(selectedRisk.status)}`}>
                <p className="text-[10px] uppercase opacity-75">Status</p>
                <p className="text-lg font-bold">{selectedRisk.status}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                <p className="text-[10px] text-slate-500 uppercase">Owner</p>
                <p className="text-sm text-white font-medium">{selectedRisk.owner}</p>
              </div>
            </div>

            {/* Description & Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div><p className="text-xs text-slate-500 uppercase mb-1">Description</p><p className="text-sm text-slate-300">{selectedRisk.description}</p></div>
                {selectedRisk.root_cause && <div><p className="text-xs text-slate-500 uppercase mb-1">Root Cause</p><p className="text-sm text-slate-300">{selectedRisk.root_cause}</p></div>}
                {selectedRisk.potential_impact && <div><p className="text-xs text-slate-500 uppercase mb-1">Potential Impact</p><p className="text-sm text-slate-300">{selectedRisk.potential_impact}</p></div>}
              </div>
              <div className="space-y-3">
                {selectedRisk.existing_controls && <div><p className="text-xs text-slate-500 uppercase mb-1">Existing Controls</p><p className="text-sm text-slate-300">{selectedRisk.existing_controls}</p></div>}
                {selectedRisk.residual_score && (
                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                    <p className="text-xs text-green-400 uppercase mb-1">Residual Risk</p>
                    <p className="text-lg font-bold text-green-400">Score: {selectedRisk.residual_score} ({selectedRisk.residual_rating})</p>
                  </div>
                )}
              </div>
            </div>

            {/* Cross-Module Integration */}
            {(selectedRisk.linked_ncr || selectedRisk.linked_works_order) && (
              <div className="border-t border-slate-800 pt-4">
                <h3 className="text-sm font-bold text-white uppercase mb-3 flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-purple-400" /> Linked Records
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {selectedRisk.linked_ncr && (
                    <div className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg" data-testid="linked-ncr-card">
                      <div className="flex items-center gap-2 mb-2">
                        <ExternalLink className="w-3 h-3 text-purple-400" />
                        <span className="text-xs text-purple-400 uppercase font-bold">Linked NCR</span>
                      </div>
                      <p className="text-sm text-white font-mono">{selectedRisk.linked_ncr}</p>
                      {linkedData?.linked_ncr_data ? (
                        <div className="mt-2 text-xs text-slate-400 space-y-0.5">
                          <p>Title: {linkedData.linked_ncr_data.title}</p>
                          <p>Severity: {linkedData.linked_ncr_data.severity} | Type: {linkedData.linked_ncr_data.type}</p>
                          <p>Status: {linkedData.linked_ncr_data.status}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-600 mt-1">Loading linked data...</p>
                      )}
                    </div>
                  )}
                  {selectedRisk.linked_works_order && (
                    <div className="p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-lg" data-testid="linked-wo-card">
                      <div className="flex items-center gap-2 mb-2">
                        <ExternalLink className="w-3 h-3 text-cyan-400" />
                        <span className="text-xs text-cyan-400 uppercase font-bold">Linked Works Order</span>
                      </div>
                      <p className="text-sm text-white font-mono">{selectedRisk.linked_works_order}</p>
                      {linkedData?.linked_order_data ? (
                        <div className="mt-2 text-xs text-slate-400 space-y-0.5">
                          <p>Part: {linkedData.linked_order_data.part_number}</p>
                          <p>Customer: {linkedData.linked_order_data.customer}</p>
                          <p>Qty: {linkedData.linked_order_data.quantity}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-600 mt-1">Loading linked data...</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions Section */}
            <div className="border-t border-slate-800 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-white uppercase">Required Actions</h3>
                <Button size="sm" onClick={() => setShowActionDialog(true)} className="bg-red-600 hover:bg-red-500" data-testid="add-action-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Action
                </Button>
              </div>
              {selectedRisk.actions?.length > 0 ? (
                <div className="space-y-2">
                  {selectedRisk.actions.map(action => (
                    <div key={action.action_id} className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-lg">
                      <button onClick={() => handleUpdateActionStatus(action.action_id, action.status === 'Completed' ? 'Pending' : 'Completed')}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${action.status === 'Completed' ? 'bg-green-500 border-green-500' : 'border-slate-600'}`}
                        data-testid={`action-toggle-${action.action_id}`}>
                        {action.status === 'Completed' && <CheckCircle2 className="w-4 h-4 text-white" />}
                      </button>
                      <div className="flex-1">
                        <p className={`text-sm ${action.status === 'Completed' ? 'text-slate-500 line-through' : 'text-white'}`}>{action.description}</p>
                        <p className="text-xs text-slate-500">{action.owner} | Due: {action.target_date}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        action.status === 'Completed' ? 'bg-green-500/20 text-green-400' : action.status === 'In Progress' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-500/20 text-slate-400'
                      }`}>{action.status}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm py-4 text-center">No actions defined</p>
              )}
            </div>

            {/* Audit Trail */}
            <div className="border-t border-slate-800 pt-4">
              <h3 className="text-sm font-bold text-white uppercase mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-400" /> Audit Trail
              </h3>
              <div className="max-h-48 overflow-y-auto space-y-1 bg-slate-800/30 rounded-lg p-3" data-testid="audit-trail">
                {selectedRisk.audit_trail?.map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-xs py-1.5 border-b border-slate-800/50 last:border-0">
                    <span className="text-slate-600 whitespace-nowrap font-mono">{new Date(entry.timestamp).toLocaleString()}</span>
                    <span className="text-slate-400 whitespace-nowrap">{entry.user}</span>
                    <span className={`font-medium whitespace-nowrap ${entry.action === 'Created' ? 'text-green-400' : entry.action === 'Updated' ? 'text-blue-400' : 'text-amber-400'}`}>{entry.action}</span>
                    <span className="text-slate-500 flex-1">{entry.details}</span>
                  </div>
                ))}
                {(!selectedRisk.audit_trail || selectedRisk.audit_trail.length === 0) && <p className="text-slate-600 text-xs">No audit entries</p>}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between pt-4 border-t border-slate-800">
              <Button variant="outline" onClick={() => handleDeleteRisk(selectedRisk.risk_id)} className="border-red-500/50 text-red-400" data-testid="delete-risk-detail-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="border-slate-600">Close</Button>
                <Button onClick={() => { setShowDetailDialog(false); openEditRisk(selectedRisk); }} className="bg-blue-600 hover:bg-blue-500" data-testid="edit-risk-detail-btn">
                  <Edit className="w-4 h-4 mr-2" /> Edit Risk
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

// ========== ACTION FORM DIALOG ==========
const ActionFormDialog = ({ open, onOpenChange, actionForm, setActionForm, handleAddAction, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-slate-900 border-red-500/30 max-w-md">
      <DialogHeader>
        <DialogTitle className="text-white uppercase tracking-wide">Add Required Action</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 pt-4">
        <div>
          <Label className="text-slate-400 uppercase text-xs">Action Description *</Label>
          <Textarea value={actionForm.description} onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" rows={3} data-testid="action-description-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-400 uppercase text-xs">Action Owner *</Label>
            <Input value={actionForm.owner} onChange={(e) => setActionForm({ ...actionForm, owner: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" data-testid="action-owner-input" />
          </div>
          <div>
            <Label className="text-slate-400 uppercase text-xs">Target Date *</Label>
            <Input type="date" value={actionForm.target_date} onChange={(e) => setActionForm({ ...actionForm, target_date: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" data-testid="action-target-date-input" />
          </div>
        </div>
        <div>
          <Label className="text-slate-400 uppercase text-xs">Notes</Label>
          <Input value={actionForm.notes} onChange={(e) => setActionForm({ ...actionForm, notes: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-600">Cancel</Button>
          <Button onClick={handleAddAction} disabled={submitting} className="bg-red-600 hover:bg-red-500" data-testid="submit-action-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Add Action
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default RiskManager;
